import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowDown, ArrowUp, ArrowUpDown, Pencil, RefreshCw, Trash2, X,
} from "lucide-react";
import { API } from "../../lib/api";
import { TIM_COLOR } from "../../lib/dashConstants";
import { fmtDate, fmtN } from "../../lib/format";

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown size={11} style={{ marginLeft: 4, opacity: 0.5 }} />;
  return dir === "asc"
    ? <ArrowUp size={11} style={{ marginLeft: 4, color: "var(--accent)" }} />
    : <ArrowDown size={11} style={{ marginLeft: 4, color: "var(--accent)" }} />;
}

function SortHeader({ col, sortBy, sortDir, onSort, children }) {
  return (
    <th
      onClick={() => onSort(col)}
      style={{ cursor: "pointer", userSelect: "none" }}
      data-testid={`sort-${col}`}
    >
      {children}
      <SortIcon active={sortBy === col} dir={sortDir} />
    </th>
  );
}

export default function DetailsTable({ filters, canEdit = false, onEdit, refreshKey = 0 }) {
  const [data, setData] = useState({ total: 0, rows: [] });
  const [skip, setSkip] = useState(0);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("tanggal_visit");
  const [sortDir, setSortDir] = useState("desc");
  const [confirmDelete, setConfirmDelete] = useState(null); // row to confirm
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, skip, limit, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      const res = await axios.get(`${API}/sales/details`, { params });
      setData(res.data);
    } catch (e) { console.error("Failed to load details", e); }
    finally { setLoading(false); }
  }, [filters, skip, limit, search, sortBy, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);
  // Reset pagination when filters / search / sort change (skip is intentionally not a dep)
  useEffect(() => { setSkip(0); }, [filters, search, sortBy, sortDir]);

  const totalPages = Math.ceil(data.total / limit) || 1;
  const currentPage = Math.floor(skip / limit) + 1;

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true); setDeleteError(null);
    try {
      await axios.delete(`${API}/sales/activities/${confirmDelete.row_index}`);
      setConfirmDelete(null);
      fetchData();
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Gagal menghapus data");
    } finally { setDeleting(false); }
  };

  return (
    <div className="card">
      <div className="card-head" style={{ flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 className="card-title">Tabel Detail Aktivitas</h3>
          <p className="card-subtitle">
            {fmtN(data.total)} aktivitas · klik header untuk sort A-Z / Z-A
            {canEdit && " · admin bisa edit/hapus per baris"}
          </p>
        </div>
        <div className="toolbar">
          <input className="search-input" placeholder="Cari sekolah / sales / issue / NPSN..."
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="details-search" />
        </div>
      </div>
      <div style={{ overflowX: "auto", border: "1px solid var(--border-2)", borderRadius: 8 }}>
        <table className="tbl" data-testid="details-table">
          <thead>
            <tr>
              <SortHeader col="tanggal_visit" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Tanggal Visit</SortHeader>
              <SortHeader col="nama_sales" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Sales</SortHeader>
              <th>Tim</th>
              <SortHeader col="status_sekolah" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Status</SortHeader>
              <SortHeader col="npsn" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>NPSN</SortHeader>
              <SortHeader col="nama_sekolah" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Nama Sekolah</SortHeader>
              <SortHeader col="tujuan_sales" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Tujuan Sales</SortHeader>
              <SortHeader col="kegiatan" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Kegiatan</SortHeader>
              <SortHeader col="dilakukan_via" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Via</SortHeader>
              <th>Detail Issue</th>
              {canEdit && <th style={{ width: 100, textAlign: "center" }}>Aksi</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={canEdit ? 11 : 10} className="empty">Memuat...</td></tr> :
              data.rows.length === 0 ? <tr><td colSpan={canEdit ? 11 : 10} className="empty">Tidak ada aktivitas yang cocok.</td></tr> :
              data.rows.map((r) => (
                <tr key={r.row_index}>
                  <td style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", color: "var(--muted)", fontSize: 12 }}>{fmtDate(r.tanggal_visit)}</td>
                  <td style={{ fontWeight: 600, color: "var(--navy)" }}>{r.nama_sales || "—"}</td>
                  <td>{r.tim_sales ? <span className="badge" style={{ background: TIM_COLOR[r.tim_sales]+"22", color: TIM_COLOR[r.tim_sales], borderColor: TIM_COLOR[r.tim_sales]+"55" }}>{r.tim_sales}</span> : "—"}</td>
                  <td><span className="badge">{r.status_sekolah || "—"}</span></td>
                  <td style={{ fontVariantNumeric: "tabular-nums", fontSize: 12, color: "var(--muted)" }}>{r.npsn || "—"}</td>
                  <td style={{ minWidth: 180 }}>{r.nama_sekolah || <span style={{ color: "var(--muted)", fontStyle: "italic", fontSize: 12 }}>(Nama sekolah kosong)</span>}</td>
                  <td>{r.tujuan_sales ? <span className={`badge ${r.tujuan_sales.includes("EFT") ? "warn" : ""}`}>{r.tujuan_sales}</span> : "—"}</td>
                  <td>{r.kegiatan || "—"}</td>
                  <td><span className="badge">{r.dilakukan_via || "—"}</span></td>
                  <td style={{ minWidth: 220, color: "var(--text)", fontSize: 12 }}>{r.detail_issue || "—"}</td>
                  {canEdit && (
                    <td style={{ whiteSpace: "nowrap", textAlign: "center" }}>
                      <button className="icon-btn" title="Edit" onClick={() => onEdit && onEdit(r)}
                        data-testid={`edit-row-${r.row_index}`}
                        style={{ color: "var(--accent)", marginRight: 4 }}>
                        <Pencil size={14} />
                      </button>
                      <button className="icon-btn" title="Hapus" onClick={() => setConfirmDelete(r)}
                        data-testid={`delete-row-${r.row_index}`}
                        style={{ color: "var(--bad)" }}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", alignItems: "center", marginTop: 12 }}>
        <div className="pagination">
          <button className="page-btn" onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0}>← Prev</button>
          <span style={{ padding: "0 10px" }}>Page <strong style={{ color: "var(--navy)" }}>{currentPage}</strong> / {totalPages}</span>
          <button className="page-btn" onClick={() => setSkip(skip + limit)} disabled={skip + limit >= data.total}>Next →</button>
        </div>
      </div>

      {confirmDelete && (
        <div className="modal-backdrop" onClick={() => !deleting && setConfirmDelete(null)} data-testid="delete-confirm-modal">
          <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 style={{ margin: 0, color: "var(--bad)" }}>
                <Trash2 size={16} style={{ verticalAlign: "-2px", marginRight: 6 }}/> Hapus Aktivitas?
              </h3>
              <button className="icon-btn" onClick={() => setConfirmDelete(null)} disabled={deleting}>
                <X size={16}/>
              </button>
            </div>
            <p style={{ fontSize: 13, color: "var(--text)" }}>
              Data ini akan dihapus permanen dari Excel dan tidak bisa dikembalikan.
            </p>
            <ul style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
              <li>Sales: <b>{confirmDelete.nama_sales}</b></li>
              <li>NPSN: <b>{confirmDelete.npsn || "—"}</b> · Sekolah: <b>{confirmDelete.nama_sekolah || "(kosong)"}</b></li>
              <li>Tanggal: <b>{fmtDate(confirmDelete.tanggal_visit)}</b></li>
              <li>Kegiatan: <b>{confirmDelete.kegiatan || "—"}</b></li>
            </ul>
            {deleteError && <div className="alert err"><X size={14}/> {deleteError}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)} disabled={deleting} style={{ flex: 1 }}>
                Batal
              </button>
              <button className="btn btn-primary" onClick={doDelete} disabled={deleting} style={{ flex: 1, background: "var(--bad)" }}
                data-testid="delete-confirm-btn">
                {deleting ? <><RefreshCw size={14} className="spin"/> Menghapus...</> : <><Trash2 size={14}/> Ya, Hapus</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}